'use client';

import React, {
  ComponentProps,
  FocusEventHandler,
  MouseEventHandler,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@wishket/design-system';
import { twMerge } from 'tailwind-merge';
import { useClickOutside } from '@wishket/yogokit';

import { AutoExpandQuoteTextarea } from '@/shared/Components';

interface DesktopMemoPopoverProps extends ComponentProps<'textarea'> {
  onSubmit: () => void;
  onClose: () => void;
}

const DesktopMemoPopover = ({
  onChange,
  onSubmit,
  onClose,
  className,
  value,
  style,
  ...rest
}: DesktopMemoPopoverProps) => {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const needAutofocus = !value;
  const [isFocused, setIsFocused] = useState(false);
  const [clickedElement, setClickedElement] = useState<HTMLElement | null>(
    null
  );

  useClickOutside(popoverRef, onClose);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    setClickedElement(event.target as HTMLElement);
  };

  const handleFocus = () => setIsFocused(true);

  const handleBlur: FocusEventHandler<HTMLTextAreaElement> &
    MouseEventHandler<HTMLButtonElement> = (e) => {
    const { relatedTarget } = e;

    if (
      relatedTarget ||
      clickedElement === cancelButtonRef.current ||
      clickedElement === submitButtonRef.current
    ) {
      return;
    }

    setIsFocused(false);
  };

  return (
    <Box
      as="span"
      ref={popoverRef}
      className={twMerge(
        'flex h-full min-h-[74px] gap-4 bg-w-white p-6 desktop:rounded-2xl desktop:shadow-graymedium',
        className
      )}
      style={style}
      data-testid="desktop-memo-popover"
      onMouseDown={handleMouseDown}
    >
      <AutoExpandQuoteTextarea
        value={value}
        onChange={onChange}
        needAutofocus={needAutofocus}
        onSubmit={onSubmit}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rest}
      />

      {isFocused && (
        <Box as="span" className="flex shrink-0 items-start gap-4">
          <button
            className="group flex h-[26px] shrink-0 items-center"
            onClick={onClose}
          >
            <Typography
              ref={cancelButtonRef}
              variant="body1"
              className="font-medium text-w-gray-600 group-hover:underline group-hover:underline-offset-2"
            >
              취소
            </Typography>
          </button>
          <button
            className="group flex h-[26px] shrink-0 items-center"
            onClick={onSubmit}
          >
            <Typography
              ref={submitButtonRef}
              variant="body1"
              className="font-medium text-primary group-hover:underline group-hover:underline-offset-2"
            >
              저장
            </Typography>
          </button>
        </Box>
      )}
    </Box>
  );
};

export default DesktopMemoPopover;
